import axios from 'axios';

const PENDING = new Map();

const TIMEOUT_MS = 5 * 60 * 1000;

// Poll-fallback interval. The primary completion path is the realtime
// ImageGenerationCompletedEvent (Pusher). Under the parallel grid's burst of
// simultaneous completions, a broadcast can be dropped — leaving a tile stuck
// until TIMEOUT_MS. We poll the job status by uuid so a stuck tile reconciles
// within ~one interval. (POR-13183)
const POLL_INTERVAL_MS = 10 * 1000;

export function registerPendingDesignJob(uuid, resolver) {
  PENDING.set(uuid, resolver);
}

// POR-13504: abandon every in-flight /networks/design job — called when the user
// hits "New" (or otherwise leaves the chat). Tells the backend to cancel them
// (so GenerateDesignConceptJob skips the compute if it hasn't started and, either
// way, suppresses its bell notification + completion broadcast — no toaster, no
// recoverable ?cs_uuid image), then rejects + clears the local awaiters so their
// chat handlers stop immediately (their late error/result writes are already
// conversation-guarded, so nothing leaks). Best-effort; never throws.
export function cancelAllPendingDesignJobs() {
  const uuids = [...PENDING.keys()];
  uuids.forEach((uuid) => {
    const entry = PENDING.get(uuid);
    if (!entry) return;
    if (entry.timeoutId) clearTimeout(entry.timeoutId);
    if (entry.pollId) clearInterval(entry.pollId);
    PENDING.delete(uuid);
    try { entry.reject(new Error('Generation cancelled')); } catch (e) { /* ignore */ }
  });
  if (!uuids.length) return Promise.resolve();
  return axios.post('/networks/design-cancel', { uuids }).catch(() => {});
}

export function unregisterPendingDesignJob(uuid) {
  const entry = PENDING.get(uuid);
  if (entry?.timeoutId) clearTimeout(entry.timeoutId);
  if (entry?.pollId) clearInterval(entry.pollId);
  PENDING.delete(uuid);
}

export function resolvePendingDesignJob(uuid, payload) {
  const entry = PENDING.get(uuid);
  if (!entry) return false;
  PENDING.delete(uuid);
  if (entry.timeoutId) clearTimeout(entry.timeoutId);
  if (entry.pollId) clearInterval(entry.pollId);
  if (payload && payload.error_message) {
    entry.reject(new Error(payload.error_message));
  } else {
    const images = payload?.images || payload?.image_urls || [];
    entry.resolve(images);
  }
  return true;
}

export function postDesignAndAwait(url, payload, isFormData = false, onUuid = null) {
  const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;
  // Derive the poll-status endpoint from the design endpoint (e.g.
  // /networks/design -> /networks/design-status). Only the design flow has one;
  // for any other url we simply skip polling.
  const statusUrl = url.replace(/\/design$/, '/design-status');
  const canPoll = statusUrl !== url;
  return axios.post(url, payload, config).then(({ data }) => {
    if (!data?.uuid) {
      throw new Error('Server did not return a generation uuid');
    }
    const uuid = data.uuid;
    if (typeof onUuid === 'function') onUuid(uuid);
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unregisterPendingDesignJob(uuid);
        reject(new Error('Image generation timed out'));
      }, TIMEOUT_MS);

      // Fallback poll: if the realtime broadcast is dropped, reconcile the job
      // by uuid. resolvePendingDesignJob clears this interval + the timeout.
      let pollId = null;
      if (canPoll) {
        pollId = setInterval(() => {
          axios.get(statusUrl, { params: { uuid } })
            .then(({ data: status }) => {
              if (status && status.done) {
                resolvePendingDesignJob(uuid, status);
              }
            })
            .catch(() => { /* ignore poll errors; realtime event / timeout still apply */ });
        }, POLL_INTERVAL_MS);
      }

      registerPendingDesignJob(uuid, { resolve, reject, timeoutId, pollId });
    });
  });
}
